/**
 * Feature configuration provider — roadmap 9.6.
 *
 * One `GET /api/v1/config/` behind TanStack Query, read once at boot and shared
 * by every consumer. It is what tells the SPA which products are running.
 *
 * **Why a request rather than a build-time `VITE_` variable.** The flag would
 * then live in two places — the Django settings that decide whether the routes
 * exist, and a bundle that decides whether the links do — and re-enabling
 * education would mean a frontend rebuild rather than a settings change.
 *
 * The endpoint is public, so this runs for anonymous visitors too. That is
 * deliberate: the announcement page is public, and it needs to know.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { api, unwrap } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'

import { DEFAULT_FEATURES, FeaturesContext, type Features } from './FeaturesContext'

async function fetchFeatures(): Promise<Features> {
  const config = await unwrap(api.GET('/api/v1/config/'))
  return {
    education: Boolean(config.features?.education),
    payments: Boolean(config.features?.payments),
  }
}

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const { data, isPending } = useQuery({
    queryKey: queryKeys.config,
    queryFn: fetchFeatures,
    // Feature state changes when the server restarts with a different setting,
    // which no amount of polling would catch mid-session anyway. Read it once.
    staleTime: Infinity,
    retry: 1,
  })

  const value = useMemo(
    () => ({
      // `DEFAULT_FEATURES` while pending and on failure — see the note there on
      // why this fails closed.
      features: data ?? DEFAULT_FEATURES,
      ready: !isPending,
    }),
    [data, isPending],
  )

  return <FeaturesContext value={value}>{children}</FeaturesContext>
}
